<template>
  <div v-bind="$attrs"
       v-on="$listeners"
       :class="$style.container"
       :hover="hover"
       :style="containerStyle"
       @mouseover="hover = true"
       @mouseleave="hover = false">
    <div :class="$style.inner"
         :hover="hover"
         :style="innerContainerStyle">
      <validation-observer ref="validator"
                           :style="validationObserverStyle">
        <validation-provider v-slot="ProviderProps"
                             :rules="`required|image|size:${maxSizeKB}`">
          <form enctype="multipart/form-data"
                :style="formStyle">
            <div :style="contentContainerStyle">
              <template v-if="uploadingFile">
                <slot name="content-uploading" />
              </template>
              <template v-else-if="uploadedFile">
                <slot name="content-uploaded" />
              </template>
              <template v-else-if="validFileSelected">
                <slot name="content-file-selected" />
              </template>
              <template v-else-if="file && !validFileSelected">
                <slot name="content-invalid-file-selected" />
              </template>
              <template v-else-if="!file && !validFileSelected">
                <input accept="image/png, image/jpeg, image/jpg"
                       ref="file"
                       type="file"
                       :class="$style.input"
                       :disabled="disabled"
                       :style="inputStyle"
                       @change="selectFile(ProviderProps, $event);">
                <div :style="contentWaitingContainerStyle">
                  <slot name="content-waiting" />
                </div>
              </template>
            </div>
          </form>
        </validation-provider>
      </validation-observer>
    </div>
  </div>
</template>

<script>
  import { DefaultPseudoClassAttributeNames,
           DefaultPseudoClassVariableNames,
           getPseudoClassesObject,
           filterObjectByArray} from '@js/vue-style-utils'
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { image, required, size } from '@js/validators/rules'

  export default {
    name: 'BaseImageUpload',
    inheritAttrs: false,
    components: {
      ValidationObserver,
      ValidationProvider,
    },
    props: {
      disabled: { type: Boolean, required: false, default: false },
      maxSizeKB: { type: Number, required: false, default: 1000, },
      resetTrigger: { type: Boolean, required: false, default: false },
      uploadedFile: { type: Boolean, required: false, default: false },
      uploadingFile: { type: Boolean, required: false, default: false },
    },
    data: function () {
      return {
        containerStyle: {
          height: '100%',
          width: '100%',
        },
        contentContainerStyle: {
          height: '100%',
          position: 'relative',
          width: '100%',
        },
        contentWaitingContainerStyle: {
          height: '100%',
          left: '50%',
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
        },
        file: null,
        formStyle: {
          height: '100%',
          width: '100%',
          position: 'relative',
        },
        hover: false,
        innerContainerStyle: {
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        },
        inputStyle: {
          cursor: 'pointer',
          height: '100%',
          left: '50%',
          opacity: 0,
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          zIndex: 99,
        },
        postfixes: ['', 'disabled', 'hover', 'selected'],
        url: null,
        validationObserverStyle: {
          height: '100%',
          width: '100%',
        },
        validFileSelected: false,
      };
    },
    created: function () {
      let classObject = getPseudoClassesObject(
        this.$attrs, DefaultPseudoClassAttributeNames,
        '--base-image-upload-container-', DefaultPseudoClassVariableNames,
        this.postfixes);
      let attrs = filterObjectByArray(
        this.$attrs, DefaultPseudoClassAttributeNames);

      this.containerStyle = {
        ...this.containerStyle,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.innerContainerStyle, DefaultPseudoClassAttributeNames,
        '--base-image-upload-inner-container-', DefaultPseudoClassVariableNames,
        this.postfixes);
      attrs = filterObjectByArray(
        this.$attrs.innerContainerStyle, DefaultPseudoClassAttributeNames);

      this.innerContainerStyle = {
        ...this.innerContainerStyle,
        ...attrs,
        ...classObject,
      };

      classObject = getPseudoClassesObject(
        this.$attrs.inputStyle, DefaultPseudoClassAttributeNames,
        '--base-image-upload-input-', DefaultPseudoClassVariableNames,
        this.postfixes);
      attrs = filterObjectByArray(
        this.$attrs.inputStyle, DefaultPseudoClassAttributeNames);

      this.inputStyle = {
        ...this.inputStyle,
        ...attrs,
        ...classObject,
      };
    },
    methods: {
      selectFile: function (providerProps, event) {
        return providerProps.validate(event).then(result => {
          if (result.valid) {
            this.handleValidFile(this.$refs.file.files[0]);
          } else {
            this.handleInvalidFile();
          }
        }).catch(error => {
          this.handleInvalidFile();
        });
      },
      reset: function () {
        this.file = null;
        this.url = null;
        this.validFileSelected = false;
        this.$emit('reset-form');
      },
      handleValidFile: function (file) {
        this.file = file;
        this.url = URL.createObjectURL(file);

        const formData = new FormData();
        formData.append('image', this.file, this.file.name);

        const payload = {
          formData: formData,
          file: this.file,
          url: this.url,
        };

        this.$emit('select-valid-file', payload);
        this.validFileSelected = true;
      },
      handleInvalidFile: function () {
        this.reset();
        this.$emit('select-invalid-file')
      },
    },
    watch: {
      resetTrigger: function (newVal, oldVal) {
        this.reset();
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--base-image-upload-container-background-color)
    border var(--base-image-upload-container-border)
    border-radius var(--base-image-upload-container-border-radius)
    border-width var(--base-image-upload-container-border-width)
    cursor var(--base-image-upload-container-cursor)
    &:hover
      background-color var(--base-image-upload-container-background-color-hover)
      border var(--base-image-upload-container-border-hover)
      border-radius var(--base-image-upload-container-border-radius-hover)
      border-width var(--base-image-upload-container-border-width-hover)
      cursor var(--base-image-upload-container-cursor-hover)
    &[selected]
      background-color var(--base-image-upload-container-background-color-selected)
      border var(--base-image-upload-container-border-selected)
      border-radius var(--base-image-upload-container-border-radius-selected)
      border-width var(--base-image-upload-container-border-width-selected)
      cursor var(--base-image-upload-container-cursor-selected)
    &[disabled]
      background-color var(--base-image-upload-container-background-color-disabled)
      border var(--base-image-upload-container-border-disabled)
      border-radius var(--base-image-upload-container-border-radius-disabled)
      border-width var(--base-image-upload-container-border-width-disabled)
      cursor var(--base-image-upload-container-cursor-disabled)

  .inner
    background-color var(--base-image-upload-inner-container-background-color)
    border var(--base-image-upload-inner-container-border)
    border-radius var(--base-image-upload-inner-container-border-radius)
    border-width var(--base-image-upload-inner-container-border-width)
    cursor var(--base-image-upload-inner-container-cursor)
    &:hover
      background-color var(--base-image-upload-inner-container-background-color-hover)
      border var(--base-image-upload-inner-container-border-hover)
      border-radius var(--base-image-upload-inner-container-border-radius-hover)
      border-width var(--base-image-upload-inner-container-border-width-hover)
      cursor var(--base-image-upload-inner-container-cursor-hover)
    &[selected]
      background-color var(--base-image-upload-inner-container-background-color-selected)
      border var(--base-image-upload-inner-container-border-selected)
      border-radius var(--base-image-upload-inner-container-border-radius-selected)
      border-width var(--base-image-upload-inner-container-border-width-selected)
      cursor var(--base-image-upload-inner-container-cursor-selected)
    &[disabled]
      background-color var(--base-image-upload-inner-container-background-color-disabled)
      border var(--base-image-upload-inner-container-border-disabled)
      border-radius var(--base-image-upload-inner-container-border-radius-disabled)
      border-width var(--base-image-upload-inner-container-border-width-disabled)
      cursor var(--base-image-upload-inner-container-cursor-disabled)

  .input
    cursor var(--base-image-upload-input-cursor)
    &:hover
      cursor var(--base-image-upload-input-cursor-hover)
    &[disabled]
      cursor var(--base-image-upload-input-cursor-disabled)
</style>
